import React, { useEffect, useMemo, useState } from "react";
import { getProducts } from "../api/productService";
import ProductCard from "../components/ProductCard";

import "./Collections.css";

const Collections = () => {
  const [products, setProducts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortOption, setSortOption] = useState("default");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);

        const data = await getProducts();

        setProducts(data);
      } catch (error) {
        console.error("Collections loading error:", error);

        setError("Unable to load products. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  /*
   * Create category list from the products
   * already stored in the database.
   */
  const categories = useMemo(() => {
    const categorySet = new Set();

    products.forEach((product) => {
      if (product.category) {
        categorySet.add(product.category);
      }
    });

    return ["All", ...Array.from(categorySet)];
  }, [products]);

  /*
   * Search + category filtering + sorting
   */
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // SEARCH

    if (searchText.trim() !== "") {
      const search = searchText.toLowerCase().trim();

      result = result.filter((product) => {
        const name = product.name?.toLowerCase() || "";

        const description = product.description?.toLowerCase() || "";

        const category = product.category?.toLowerCase() || "";

        return (
          name.includes(search) ||
          description.includes(search) ||
          category.includes(search)
        );
      });
    }

    // CATEGORY

    if (selectedCategory !== "All") {
      result = result.filter(
        (product) => product.category === selectedCategory,
      );
    }

    // SORTING

    if (sortOption === "low-high") {
      result.sort((a, b) => {
        const priceA = Number(a.salePrice ?? a.price);

        const priceB = Number(b.salePrice ?? b.price);

        return priceA - priceB;
      });
    }

    if (sortOption === "high-low") {
      result.sort((a, b) => {
        const priceA = Number(a.salePrice ?? a.price);

        const priceB = Number(b.salePrice ?? b.price);

        return priceB - priceA;
      });
    }

    if (sortOption === "name") {
      result.sort((a, b) => a.name.localeCompare(b.name));
    }

    return result;
  }, [products, searchText, selectedCategory, sortOption]);

  return (
    <main className="collections-page">
      {/* HEADER */}

      <section className="collections-header">
        <p className="collections-eyebrow">STAR GOLD</p>

        <h1>Our Collections</h1>

        <p>Discover elegant jewellery pieces for every special occasion.</p>
      </section>

      {/* CONTROLS */}

      <section className="collection-controls">
        {/* SEARCH */}

        <div className="collection-search">
          <input
            type="text"
            placeholder="Search jewellery..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>

        {/* CATEGORIES */}

        <div className="collection-categories">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className={
                selectedCategory === category
                  ? "category-button active"
                  : "category-button"
              }
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* SORT */}

        <div className="collection-sort">
          <label htmlFor="sortProducts">Sort:</label>

          <select
            id="sortProducts"
            value={sortOption}
            onChange={(event) => setSortOption(event.target.value)}
          >
            <option value="default">Featured</option>

            <option value="low-high">Price: Low to High</option>

            <option value="high-low">Price: High to Low</option>

            <option value="name">Name: A to Z</option>
          </select>
        </div>
      </section>

      {/* PRODUCT COUNT */}

      {!loading && !error && (
        <div className="collection-result-info">
          <span>
            {filteredProducts.length}{" "}
            {filteredProducts.length === 1 ? "product" : "products"}
          </span>

          {(searchText || selectedCategory !== "All") && (
            <button
              type="button"
              onClick={() => {
                setSearchText("");
                setSelectedCategory("All");
                setSortOption("default");
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* LOADING */}

      {loading && (
        <div className="collections-message">
          <p>Loading our collection...</p>
        </div>
      )}

      {/* ERROR */}

      {!loading && error && (
        <div className="collections-message error">
          <p>{error}</p>
        </div>
      )}

      {/* NO PRODUCTS */}

      {!loading && !error && products.length === 0 && (
        <div className="collections-message">
          <p>No products available yet.</p>
        </div>
      )}

      {/* NO SEARCH RESULTS */}

      {!loading &&
        !error &&
        products.length > 0 &&
        filteredProducts.length === 0 && (
          <div className="collections-message">
            <h2>No jewellery found</h2>

            <p>Try another search or category.</p>

            <button
              type="button"
              onClick={() => {
                setSearchText("");
                setSelectedCategory("All");
              }}
            >
              Show All Products
            </button>
          </div>
        )}

      {/* PRODUCTS */}

      {!loading && !error && filteredProducts.length > 0 && (
        <section className="collections-grid">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </section>
      )}
    </main>
  );
};

export default Collections;
